import { type FocusAjaxError } from "@focus/interfaces";
import { getErrorMessage } from "@focus/services/lib/helpers/errorParser";
import { catchError, map, mergeMap, of, startWith, tap } from "rxjs";
import { type AjaxResponse } from "rxjs/ajax";
import KeycloakManager from "../keycloak";
import {
  setDownloadFileError,
  setDownloadFileFinish,
  setDownloadFilePending,
  setDownloadFileZipError,
  setDownloadFileZipFinish,
  setDownloadFileZipPending,
  setUploadFileError,
  setUploadFileFinish,
  setUploadFilePending,
} from "./actions";
import { actionTypes } from "./actionTypes";

import { Intent } from "@blueprintjs/core";
import { ofType } from "redux-observable";
import { showNotification } from "../../store/toasterDuck";

const REG_CONTENT_DISP = /filename="(.*)"/;

function getFileName(contentDisposition: string) {
  return REG_CONTENT_DISP.exec(contentDisposition)[1] || "test.zip";
}

const saveFile = (saveAs) => (filename: string, response: AjaxResponse) => {
  const receivedFileName = getFileName(filename);
  saveAs(response.response, receivedFileName);
};

export const uploadOrbit =
  (uploadOrbitUrl) =>
  (action$, _, { ajax }) => {
    return action$.pipe(
      ofType(actionTypes.UPLOAD),
      mergeMap((action) => {
        const fd = new FormData();
        action.payload?.files?.forEach((fileObject) => {
          fd.append("orbit-file", fileObject.file);
        });
        return ajax({
          url: uploadOrbitUrl(action.payload.operator),
          headers: { Authorization: `Bearer ${KeycloakManager.getToken()}` },
          method: "POST",
          body: fd,
        }).pipe(
          map(() => setUploadFileFinish(action.payload)),
          catchError((error: FocusAjaxError) =>
            of(setUploadFileError(action.payload, getErrorMessage(error)))
          ),
          startWith(setUploadFilePending(action.payload))
        );
      })
    );
  };

export const showUploadErrorPopup =
  () =>
  (action$, _, { ajax }) => {
    return action$.pipe(
      ofType(actionTypes.UPLOAD_ERROR),
      mergeMap((action) =>
        of(
          showNotification({
            intent: Intent.DANGER,
            message: `An Error occured while uploading: ${action.error}`,
          })
        )
      )
    );
  };
  
export const showUploadOkPopup =
  () =>
  (action$, _, { ajax }) => {
    return action$.pipe(
      ofType(actionTypes.UPLOAD_FINISH),
      mergeMap((action) =>
        of(
          showNotification({
            intent: Intent.SUCCESS,
            message: `Files uploaded successfully`,
          })
        )
      )
    );
  };

export const downloadZipOrbit =
  (downloadOrbitUrl, saveAs) =>
  (action$, _, { ajax }) => {
    return action$.pipe(
      ofType(actionTypes.DOWNLOAD_ZIP),
      mergeMap((action) => {
        return ajax({
          url: downloadOrbitUrl(action.payload),
          headers: {
            "content-type": "application/zip",
            Authorization: `Bearer ${KeycloakManager.getToken()}`,
          },
          responseType: "blob",
        }).pipe(
          tap((res) =>
            saveFile(saveAs)(
              res.xhr.getResponseHeader("content-disposition"),
              res
            )
          ),
          map(() => setDownloadFileZipFinish(action.payload)),
          catchError((error: FocusAjaxError) =>
            of(setDownloadFileZipError(action.payload, getErrorMessage(error)))
          ),
          startWith(setDownloadFileZipPending(action.payload))
        );
      })
    );
  };

export const downloadOrbit =
  (downloadOrbitUrl, saveAs) =>
  (action$, _, { ajax }) => {
    return action$.pipe(
      ofType(actionTypes.DOWNLOAD),
      mergeMap((action) => {
        return ajax({
          url: downloadOrbitUrl(action.payload),
          headers: {
            "content-type": "application/octet-stream",
            Authorization: `Bearer ${KeycloakManager.getToken()}`,
          },
          responseType: "blob",
        }).pipe(
          tap((res) =>
            saveFile(saveAs)(
              res.xhr.getResponseHeader("content-disposition"),
              res
            )
          ),
          map(() => setDownloadFileFinish(action.payload)),
          catchError((error: FocusAjaxError) =>
            of(setDownloadFileError(action.payload, getErrorMessage(error)))
          ),
          startWith(setDownloadFilePending(action.payload))
        );
      })
    );
  };
